import { mqttStore } from '@/store/mqtt'
import { storeToRefs } from 'pinia'
import { appStore } from '@/store/app'
import { streamStore } from '@/store/stream'
import { uuid } from '@/helpers'
import { viewsStore } from '@/store/views'
import type { JobTelemetry } from '@/types/jobTelemetry'
import { isEqual } from 'lodash'
import log from '@/utils/useLogger'
import { useAudioController } from './useAudioController'
import { useBrowserNotification } from './useBrowserNotification'
import { type MissionUpdate } from '@/types/mqtt'
import { useUser } from './useOrgUsers'
import { useUserStore } from '@/store/userStore'
import type { TakeoverRequest, TakeoverResponse } from './Device/DeviceTakeover'
import DeviceTakeover from './Device/DeviceTakeover'
import usePresence from './usePresence'
import { useQueryClient } from '@tanstack/vue-query'
import { useLaunchMission } from './FlightTools/useLaunchMission'
import router from '@/router'

const useMqtt = () => {
  const mqttInstance = mqttStore()
  const appInstance = appStore()
  const streamInstance = streamStore()
  const viewsInstance = viewsStore()
  const userStore = useUserStore()
  const { applyPresenceMessage, startHeartbeat, stopHeartbeat } = usePresence()
  const queryClient = useQueryClient()
  const { downloadFile } = useLaunchMission()

  const { disconnectShadows, connectToShadows, connectSession, disconnectSession } =
    mqttInstance
  const { setInfo } = viewsInstance
  const {
    setProperty,
    addNotifications,
    mergeHighFrequencyTelemetry,
    mergeLowFrequencyTelemetry,
    handleDeviceHealth,
  } = appInstance
  const { initializeExposure, initializeThermal, initializeFocus, initializeActiveLens } =
    streamInstance
  const { userId, userName } = storeToRefs(userStore)
  const { isAutomationShow, preflightChecklistMode, isPreflightOpen, showDeviceTakeoverModal } =
    storeToRefs(viewsInstance)
  const {
    telemetry,
    dockStatus,
    selectedRC,
    selectedDock,
    rcState,
    recordingStatus,
    goToLocationActive,
    gtl,
    jobTelemetry,
    selectedDevice,
    selectedPortal,
    portalStatus,
  } = storeToRefs(appInstance)
  const { client, sessionClient, messageHandler, takeoverRequest, realtimeDegraded } =
    storeToRefs(mqttInstance)
  const { gimbalControls, cameraState } = storeToRefs(streamInstance)

  const { playAudio, stopAudio, cleanup } = useAudioController()
  const { notify: notifyBrowser } = useBrowserNotification()
  const { triggerLogout, invalidate } = useUser()
  const { sendTakeOverResponse, handleTakeoverResponse } = DeviceTakeover()
  let lastNotification: { notification: string; timestamp: number } | null = null

  const createTopicHandlers = () => {
    const handlers = new Map<
      string | ((topic: string) => boolean),
      (topic: string, message: Buffer) => Promise<void> | void
    >()

    // User-specific handlers
    handlers.set(
      (topic: string) => !!(userName.value && topic === `logout/${userName.value}`),
      async () => {
        triggerLogout()
      },
    )

    handlers.set(
      (topic: string) => topic === `device-group-takeover-request/${userId.value}`,
      async (_, message) => {
        const response: TakeoverRequest = JSON.parse(message.toString())
        log.info(`Device group ${response?.type} request`, response)
        if (telemetry.value?.aircraftState?.isFlying) {
          await sendTakeOverResponse(response, 'decline', 'flying')
          return
        }
        const isHandover = response?.type === 'Handover'
        notifyBrowser({
          title: `Device Group ${response?.type ?? 'Takeover'} Request`,
          body: isHandover
          ? `${response?.initiator} wants to hand over "${response?.deviceGroupName}" to you.`
          : `${response?.requestingUserName} is requesting to take over "${response?.deviceGroupName}".`,
          tag: 'device-group-takeover',
          autoCloseMs: 30000,
        })
        
        takeoverRequest.value = response
        showDeviceTakeoverModal.value = true
        log.info('Showing device takeover modal')
      },
    )

    handlers.set(
      (topic: string) => topic === `device-group-takeover-response/${userId.value}`,
      async (_, message) => {
        const response: TakeoverResponse = JSON.parse(message.toString())
        log.info(`Device group ${response?.request?.type} response`, response)
        await handleTakeoverResponse(response)
      },
    )

    handlers.set(
      (topic: string) => topic === `device-group-assignment-changed/${userId.value}`,
      async (_, message) => {
        const assignmentData = JSON.parse(message.toString())
        log.info('Device group assignment changed', assignmentData)

        const { deviceGroupId, deviceGroupName, newOwnerId } = assignmentData
        if (newOwnerId == userId.value) {
          setInfo(
            'Device Group Assigned Successfully',
            `You have been successfully assigned to the device group "${deviceGroupName}".`,
          )
          notifyBrowser({
            title: 'Device Group Assigned',
            body: `You have been assigned to the device group "${deviceGroupName}".`,
            tag: 'device-group-assignment',
          })
        } else {
          setInfo(
            'Device Group Access Removed',
            `Your access to "${deviceGroupName}" has been removed.`,
          )
          // Important passive event — surface it even when the user is away (FLY3D-866).
          notifyBrowser({
            title: 'Device Group Access Removed',
            body: `Your access to "${deviceGroupName}" has been removed.`,
            tag: 'device-group-assignment',
          })
          if (selectedDevice.value === deviceGroupId) selectedDevice.value = null
        }
        invalidate.deviceGroupIds()
        invalidate.users()
      },
    )

    // FLY3D-1029: presence.
    //
    // The ping/pong handlers that used to be here are gone. They answered
    // "is this operator online?" with a 5 second request/response probe, which
    // reported logged-on pilots as offline whenever their MQTT client happened
    // to be rebuilding (which it did on every device switch).
    //
    // Presence now arrives as retained state on `presence/{org}/{userId}`,
    // published by each dashboard on connect and by the broker's Last Will if
    // a browser dies ungracefully. Subscribing delivers every colleague's
    // current state immediately, so there is nothing to time out.
    handlers.set(
      (topic: string) => topic.startsWith('presence/') && topic.split('/').length === 3,
      async (topic, message) => {
        applyPresenceMessage(topic, message.toString())
      },
    )

    // Annotation share notification handler
    handlers.set(
      (topic: string) => topic === `annotation-shared/${userId.value}`,
      async (_, message) => {
        try {
          const shareData = JSON.parse(message.toString())
          log.info('Received annotation share notification', shareData)

          // Show notification to user
          addNotifications({
            notification: `${shareData.sharedBy} shared a Point of Interest "${shareData.annotationLabel}" with you`,
            type: 'info',
            id: crypto.randomUUID(),
          })

          // Invalidate shared annotations query to trigger refetch
          queryClient.invalidateQueries({ queryKey: ['annotations', 'shared', userId.value] })
        } catch (error) {
          log.error('Error handling annotation share notification:', error)
        }
      },
    )

    // FLY3D-914: sharing/ACL revocation handler (contract §6).
    // The backend pushes revocation/{userId} when a grant the user relied on is
    // revoked. We drop the cached decisions + "shared with me", tell the user, and
    // (on a full revocation) route to the Access Revoked page.
    handlers.set(
      (topic: string) => topic === `revocation/${userId.value}`,
      async (_, message) => {
        try {
          const data = JSON.parse(message.toString()) as {
            scope?: 'all' | 'specific'
            reason?: string
          }
          log.info('Received sharing revocation', data)

          // Invalidate all cached sharing state so the next read re-checks access.
          queryClient.invalidateQueries({ queryKey: ['can'] })
          queryClient.invalidateQueries({ queryKey: ['shared-with-me'] })
          queryClient.invalidateQueries({ queryKey: ['sharing'] })

          addNotifications({
            notification: data.reason || 'Your access to a shared resource was revoked.',
            type: 'warning',
            id: crypto.randomUUID(),
          })

          // A full revocation means the user lost broad access — send them to the
          // Access Revoked page. A specific revocation just refreshes caches above.
          if (data.scope === 'all') {
            router.push('/share/revoked')
          }
        } catch (error) {
          log.error('Error handling sharing revocation:', error)
        }
      },
    )

    // RC/Telemetry handlers
    handlers.set(
      (topic: string) => topic === `${selectedRC.value}/TELEMETRY`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        mergeLowFrequencyTelemetry(response)
        if (
          telemetry.value &&
          !Object.values(gimbalControls.value).every((value) => value !== null)
        ) {
          initializeExposure()
          initializeFocus()
          initializeThermal()
          initializeActiveLens()
        }
      },
    )
    // Media upload event handler
    handlers.set(
      (topic: string) => topic === `${selectedRC.value}/MEDIA_UPLOAD_EVENT`,
      (_, message) => {
        const response = JSON.parse(message.toString())

        const { flight_task, file } = response.data ?? {}
        if (!flight_task) return

        const uploaded = flight_task.uploaded_file_count ?? 0
        const expected = flight_task.expected_file_count ?? 0

        viewsInstance.mediaUploadProgress = {
          uploadedCount: uploaded,
          expectedCount: expected,
          currentFileName: file?.name ?? '',
          flightId: file?.ext?.flight_id ?? '',
        }

        if (uploaded < expected) {
          viewsInstance.isMediaUploading = true
        } else {
          setTimeout(() => {
            viewsInstance.isMediaUploading = false
            viewsInstance.mediaUploadProgress = null
          }, 5000)
        }
      },
    )
    handlers.set(
      (topic: string) => topic === `${selectedRC.value}/TELEMTRY_HIGH_FREQ`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        mergeHighFrequencyTelemetry(response)
      },
    )

    // Dock state handlers
    handlers.set(
      (topic: string) =>
        !!(
          selectedDock.value &&
          selectedDock.value !== '0000' &&
          topic === `${selectedDock.value}/DOCK_STATE_DATA`
        ),
      (_, message) => {
        const response = JSON.parse(message.toString())
        setProperty('dockState', response)
      },
    )

    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedDock.value}/shadow/name/DOCK_STATE/get/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        dockStatus.value = response.state.reported?.connection_status
      },
    )

    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedDock.value}/shadow/name/DOCK_STATE/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        if (response.state.reported?.connection_status) {
          dockStatus.value = response.state.reported?.connection_status
        }
      },
    )

    // Dock commands
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedDock.value}/shadow/name/DOCK_COMMANDS/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported
        if (reported?.status === 'FAILED' || reported?.status === 'TIMEOUT') {
          const id = uuid(10)
          let notificationMessage = reported.message || 'Dock command failed'

          if (reported?.status === 'TIMEOUT' && reported?.success === false) {
            notificationMessage = `${reported.command || 'Command'} timed out`
          }

          addNotifications({
            notification: notificationMessage,
            type: 'error',
            id,
          })
        }
      },
    )

    // Automation handlers
    handlers.set(
      (topic: string) =>
        topic ===
        `$aws/things/${selectedDock.value}/shadow/name/AUTOMATION_COMMAND/update/accepted`,
      () => {
        // TODO: Implement automation command handling if needed
      },
    )

    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedDock.value}/shadow/name/AUTOMATION_STATE/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported
        if (reported.automation_state) {
          setProperty('automation_state', reported.automation_state)
          isAutomationShow.value = true

          const postTakeoffAction = reported.automation_state.find(
            (item: any) =>
              item.name === 'POST_TAKEOFF_SUCCESSFUL_ACTION' && item.status === 'SUCCESS',
          )

          if (postTakeoffAction) {
            setProperty('automation_state', null)
            isAutomationShow.value = false
          }
        }
        if (reported.preflight_checklist) {
          preflightChecklistMode.value = reported.preflight_checklist
          isPreflightOpen.value = true
          isAutomationShow.value = false
        } else {
          preflightChecklistMode.value = null
          isPreflightOpen.value = false
        }
      },
    )

    // Notification handlers
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/NOTIFICATIONS/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state?.reported
        handleNotification(reported)
      },
    )

    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedDock.value}/shadow/name/NOTIFICATIONS/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state?.reported
        handleNotification(reported)
      },
    )

    // Mission commands
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/MISSION_COMMAND/update/accepted`,
      async (_, message) => {
        const missionUpdate = JSON.parse(message.toString()) as MissionUpdate
        if (!missionUpdate.state.reported) return

        const id = uuid(10)
        const missionCommand = missionUpdate.state.reported?.mission_command
        const status = missionUpdate.state.reported?.status
        if (missionCommand == 'UPLOAD') {
          addNotifications({ notification: 'Mission Uploaded to Drone', type: 'success', id })
          goToLocationActive.value = false
          return
        }

        if (missionCommand == 'UPLOAD_RC' && missionUpdate.state.reported.job_status == 'SUCCESS') {
          const { waylines_s3_bucket, waylines_s3_key } = missionUpdate.state.reported.result
          downloadFile(waylines_s3_bucket, waylines_s3_key)
          addNotifications({
            notification: 'Mission Uploaded to RC',
            type: 'success',
            id,
          })
          goToLocationActive.value = false
          gtl.value = false
          return
        }

        if (status == '400') {
          addNotifications({
            notification: missionUpdate.state.reported.dji_error
              ? `${missionUpdate.state.reported.dji_error}`
              : `Sorry, an error occurred`,
            type: 'error',
            id,
          })
          return
        }
      },
    )

    // Job telemetry
    handlers.set(
      (topic: string) => topic.endsWith('/ACTIVE_JOBS_TELEMETRY'),
      (_, message) => {
        const response: JobTelemetry = JSON.parse(message.toString())
        if (!isEqual(jobTelemetry.value, response.active_jobs))
          jobTelemetry.value = response.active_jobs
      },
    )

    // RC state handlers
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/RC_STATE/get/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        rcState.value = response.state.reported?.connection_status
      },
    )

    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/RC_STATE/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        rcState.value = response.state.reported?.connection_status
      },
    )

    // Camera commands
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/CAMERA_COMMANDS/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported
        const id = uuid(10)

        if (reported) {
          if (reported?.statusRecord) {
            if (reported?.statusRecord == '400') {
              addNotifications({
                notification: reported?.djiErrorRecord,
                type: 'error',
                id,
              })
            } else if (reported?.statusRecord == '200') {
              addNotifications({
                notification:
                  reported.record_command == 'START' ? 'Recording started' : 'Recording stopped',
                type: 'success',
                id,
              })
              recordingStatus.value = reported.record_command == 'START' ? true : false
            }
          } else if (reported?.statusPC) {
            if (reported?.statusPC == '400') {
              addNotifications({
                notification: reported?.djiErrorPC,
                type: 'error',
                id,
              })
            } else if (reported?.statusPC == '200') {
              addNotifications({
                notification: 'Camera shot taken',
                type: 'success',
                id,
              })
            }
          }
        }
      },
    )

    // Camera config
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/CAMERA_CONFIG/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported
        const id = uuid(10)
        if (reported) {
          if (reported?.statusCVS == '400') {
            addNotifications({
              notification: reported?.djiErrorCVS,
              type: 'error',
              id,
            })
          } else if (reported?.statusCVS == '200') {
            // addNotifications({
            //   notification: reported.cameraVideoSource + " Mode Active",
            //   type: "success",
            //   id,
            // });
          }

          if (reported?.statusCM == '400') {
            addNotifications({
              notification: reported?.djiErrorCM,
              type: 'error',
              id,
            })
          } else if (reported?.statusCM == '200') {
            addNotifications({
              notification: reported.camera_mode + ' Mode Active',
              type: 'success',
              id,
            })
          }
        }
      },
    )

    // Camera state
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/CAMERA_STATE/get/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported

        if (reported) {
          cameraState.value = reported
        }
      },
    )

    // Gimbal command
    handlers.set(
      (topic: string) =>
        topic === `$aws/things/${selectedRC.value}/shadow/name/GIMBAL_COMMAND/update/accepted`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        const reported = response.state.reported

        if (reported?.statusTiltAngle == '400') {
          addNotifications({
            notification: 'Sorry, an error occurred',
            type: 'error',
            id: uuid(10),
          })
        }
      },
    )

    // Weather data
    handlers.set(
      (topic: string) => topic === `${selectedDock.value}/WEATHER_DATA`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        if (response && response.weatherStateData) {
          setProperty('weatherData', response.weatherStateData)
        }
      },
    )

    // Device health handlers
    handlers.set(
      (topic: string) => topic === `${selectedRC.value}/DEVICE_HEALTH`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        if (response) {
          handleDeviceHealth(response)
        }
      },
    )

    handlers.set(
      (topic: string) => topic === `${selectedDock.value}/DEVICE_HEALTH`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        if (response) {
          handleDeviceHealth(response)
        }
      },
    )
    // Slinktech portal status
    handlers.set(
      (topic: string) => topic === `${selectedPortal.value}/PORTAL_STATUS`,
      (_, message) => {
        if (selectedPortal.value === '0000') return
        const response = JSON.parse(message.toString())
        portalStatus.value = response.status
      },
    )
    // Audio triggers
    handlers.set(
      (topic: string) => topic === `${selectedRC.value}/Audio_trigger`,
      (_, message) => {
        const response = JSON.parse(message.toString())
        switch (response.command.toUpperCase()) {
          case 'START':
            playAudio(response.audioID)
            break
          case 'STOP':
            stopAudio(response.audioID)
            break
          default:
            log.error('Unknown audio command:', response.command)
        }
      },
    )

    return handlers
  }

  const setUpHandler = () => {
    log.info('Setting up MQTT message handler for app')
    // Detach from both clients before rebinding, or a rebuild leaves a stale
    // listener behind and every message is handled twice.
    if (messageHandler.value) {
      client.value?.off('message', messageHandler.value)
      sessionClient.value?.off('message', messageHandler.value)
    }
    const topicHandlers = createTopicHandlers()
    messageHandler.value = async (topic: string, message: Buffer) => {
      try {
        for (const [matcher, handler] of topicHandlers) {
          const shouldHandle = typeof matcher === 'function' ? matcher(topic) : matcher === topic
          if (shouldHandle) {
            await handler(topic, message)
            return
          }
        }
        log.debug('No handler found for topic:', topic)
      } catch (error) {
        log.error('Error handling MQTT message:', error, 'Topic:', topic)
      }
    }
    // FLY3D-1029: one handler table, two clients. The device client carries
    // telemetry and shadows; the session client carries the user-scoped topics
    // (takeover, assignment, presence). Both must be bound, or whichever is
    // missed goes silently unhandled.
    client.value?.on('message', messageHandler.value)
    sessionClient.value?.on('message', messageHandler.value)
  }
  const handleNotification = (reported: any) => {
    // Skip if reported is invalid
    if (!reported) return
    const id = uuid(10)
    const currentTime = Date.now()
    const timeThreshold = 60 * 1000 // 60 seconds
    if (
      lastNotification &&
      reported.notification === lastNotification.notification &&
      currentTime - lastNotification.timestamp < timeThreshold
    )
      return
    // Update lastNotification with the current one and timestamp
    lastNotification = { notification: reported.notification, timestamp: currentTime }
    // Add the new notification
    addNotifications({ ...reported, id })
  }
  return {
    takeoverRequest,
    messageHandler,
    client,
    sessionClient,
    realtimeDegraded,
    setUpHandler,
    disconnectShadows,
    connectToShadows,
    connectSession,
    disconnectSession,
    startHeartbeat,
    stopHeartbeat,
    cleanup,
  }
}
export default useMqtt
